import KpiChart from "@app/components/kpi-chart";
import CohortCurveChart from "./cohort-curve-chart";
import ExplainButton from "@app/components/explain/explain-button";
import type { ExplainMetric } from "@app/components/explain/explain-panel";
import { useState } from "react";
import { graphql, usePreloadedQuery } from "react-relay";
import type { PreloadedQuery } from "react-relay";

import type { mrrPage_Query } from "@app/queries/mrrPage_Query.graphql";

export const mrrQuery = graphql`
  query mrrPage_Query($from: String, $to: String) {
    unitEconomics {
      arpu
      grossMarginTtm
      monthlyRevenueChurnTtm
      ltv
      cacTtm
      ltvToCac
      paybackMonths
      cohortCurves {
        label
        size
        points {
          month
          value
        }
      }
      churnCohortCurves {
        label
        size
        points {
          month
          value
        }
      }
      cacPaybackCohortCurves {
        label
        size
        points {
          month
          value
        }
      }
      conversionCohortCurves {
        label
        size
        points {
          month
          value
        }
      }
      cacPaybackSeries {
        punctual {
          month
          value
        }
        ttm {
          month
          value
        }
      }
      cacPaybackCohorts {
        monthly {
          label
          paybackMonths
        }
        quarterly {
          label
          paybackMonths
        }
        yearly {
          label
          paybackMonths
        }
      }
    }
    mrr(from: $from, to: $to) {
      newMrr {
        month
        value
      }
      expansionMrr {
        month
        value
      }
      reactivationMrr {
        month
        value
      }
      contractionMrr {
        month
        value
      }
      churnedMrr {
        month
        value
      }
      netNewMrr {
        month
        value
      }
      endingMrr {
        month
        value
      }
      smsRevenue {
        month
        value
      }
      oneTimeRevenue {
        month
        value
      }
      arpu {
        month
        value
      }
      nrrTtm {
        month
        value
      }
      grrTtm {
        month
        value
      }
      quickRatio {
        month
        value
      }
      activeCustomers {
        month
        value
      }
      newCustomers {
        month
        value
      }
    }
  }
`;

type Point = {
  readonly month: string;
  readonly value: number | null | undefined;
};

function lastValue(points: ReadonlyArray<Point>): number | null {
  for (let i = points.length - 1; i >= 0; i--) {
    const value = points[i].value;
    if (value != null) return value;
  }
  return null;
}

export default function MrrPage({
  queryRef,
}: {
  queryRef: PreloadedQuery<mrrPage_Query>;
}) {
  const data = usePreloadedQuery(mrrQuery, queryRef);
  const mrr = data.mrr;

  if (mrr.endingMrr.length === 0) {
    return (
      <div className="p-6 text-gray-500 space-y-2">
        <h1 className="text-xl font-bold text-gray-900">MRR</h1>
        <p>
          No Stripe data synced yet. Set{" "}
          <code className="font-mono">STRIPE_API_KEY</code> in{" "}
          <code className="font-mono">.env</code> and run{" "}
          <code className="font-mono">rake stripe:sync</code>.
        </p>
      </div>
    );
  }

  const nrr = lastValue(mrr.nrrTtm);
  const grr = lastValue(mrr.grrTtm);
  const arpu = lastValue(mrr.arpu);
  const ending = lastValue(mrr.endingMrr);
  const active = lastValue(mrr.activeCustomers);
  const sms = lastValue(mrr.smsRevenue);

  const cards: {
    label: string;
    value: string;
    hint: string;
    explain?: ExplainMetric;
  }[] = [
    {
      label: "MRR",
      value: ending == null ? "—" : `$${ending.toLocaleString()}`,
      hint: "subscription, latest month",
    },
    {
      label: "NRR (TTM)",
      value: nrr == null ? "—" : `${(nrr * 100).toFixed(0)}%`,
      hint: "net revenue retention",
      explain: "NRR",
    },
    {
      label: "GRR (TTM)",
      value: grr == null ? "—" : `${(grr * 100).toFixed(0)}%`,
      hint: "gross revenue retention",
      explain: "GRR",
    },
    {
      label: "ARPU",
      value: arpu == null ? "—" : `$${arpu.toFixed(0)}`,
      hint: "per active customer",
      explain: "ARPU",
    },
    {
      label: "SMS (PAYG)",
      value: sms == null ? "—" : `$${Math.round(sms).toLocaleString()}`,
      hint: "non-recurring, latest",
    },
    {
      label: "Customers",
      value: active == null ? "—" : active.toFixed(0),
      hint: "active, latest month",
    },
  ];

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-6">
      <h1 className="text-xl font-bold">MRR movements</h1>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        {cards.map((card) => (
          <div key={card.label} className="bg-white rounded border p-4">
            <div className="flex items-start justify-between gap-2">
              <p className="text-xs text-gray-500">{card.label}</p>
              {card.explain && <ExplainButton metric={card.explain} title={card.label} />}
            </div>
            <p className="text-2xl font-bold mt-1">{card.value}</p>
            <p className="text-[11px] text-gray-400 mt-1">{card.hint}</p>
          </div>
        ))}
      </div>

      <KpiChart
        title="MRR waterfall (gains above zero, losses below; line = net new MRR)"
        series={[
          {
            name: "New",
            points: mrr.newMrr,
            type: "bar",
            stack: "mrr",
            color: "#16a34a",
          },
          {
            name: "Expansion",
            points: mrr.expansionMrr,
            type: "bar",
            stack: "mrr",
            color: "#4ade80",
          },
          {
            name: "Reactivation",
            points: mrr.reactivationMrr,
            type: "bar",
            stack: "mrr",
            color: "#86efac",
          },
          {
            name: "Contraction",
            points: mrr.contractionMrr,
            type: "bar",
            stack: "mrr",
            color: "#fb923c",
          },
          {
            name: "Churned",
            points: mrr.churnedMrr,
            type: "bar",
            stack: "mrr",
            color: "#dc2626",
          },
          {
            name: "Net new",
            points: mrr.netNewMrr,
            type: "line",
            color: "#111827",
          },
        ]}
        format="money"
        onMonthClick={() => {}}
      />

      <p className="text-xs text-gray-500 -mt-3">
        Movements are computed from billed invoices (cash basis). Stripe's
        dashboard reports <span className="font-medium">contracted</span> MRR,
        where mid-cycle plan and seat changes — billed here without proration —
        are recorded the moment they happen. Its gross
        New/Expansion/Contraction/Churn therefore run roughly 2× these figures;
        net new MRR and total MRR match.
      </p>

      <UnitEconomicsSection economics={data.unitEconomics} />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <KpiChart
          title="Recurring MRR vs pay-as-you-go (SMS, one-time)"
          series={[
            {
              name: "Subscription MRR",
              points: mrr.endingMrr,
              type: "bar",
              color: "#2563eb",
            },
            {
              name: "SMS (PAYG)",
              points: mrr.smsRevenue,
              type: "line",
              color: "#0d9488",
            },
            {
              name: "One-time",
              points: mrr.oneTimeRevenue,
              type: "line",
              color: "#d97706",
            },
          ]}
          format="money"
          onMonthClick={() => {}}
        />
        <KpiChart
          title="NRR / GRR (trailing 12 months)"
          series={[
            { name: "NRR", points: mrr.nrrTtm, type: "line", color: "#16a34a" },
            { name: "GRR", points: mrr.grrTtm, type: "line", color: "#d97706" },
          ]}
          format="percent"
          onMonthClick={() => {}}
        />
        <KpiChart
          title="ARPU"
          series={[
            { name: "ARPU", points: mrr.arpu, type: "line", color: "#7c3aed" },
          ]}
          format="money"
          onMonthClick={() => {}}
        />
        <KpiChart
          title="Customers (active + new)"
          series={[
            {
              name: "Active",
              points: mrr.activeCustomers,
              type: "line",
              color: "#0d9488",
            },
            {
              name: "New",
              points: mrr.newCustomers,
              type: "bar",
              color: "#16a34a",
            },
          ]}
          format="number"
          onMonthClick={() => {}}
        />
      </div>
    </div>
  );
}

// CAC payback charts are read at board cadence — monthly (responsive),
// quarterly, or yearly. The blended time series thins to period-end months the
// way the dashboard Rule of 40 chart does; the cohort chart just swaps which
// backend-grouped cohort set it renders.
type Granularity = "monthly" | "quarterly" | "yearly";
const GRANULARITIES: ReadonlyArray<Granularity> = [
  "monthly",
  "quarterly",
  "yearly",
];

// Keep period-end months (Mar/Jun/Sep/Dec for quarterly, Dec for yearly), always
// retaining the latest month. The punctual line is a spot reading and the TTM
// line is already a trailing-twelve window, so thinning to period ends only
// changes sampling density, not the metric.
function periodEndMonths(
  months: ReadonlyArray<string>,
  granularity: Granularity
): Set<string> {
  if (granularity === "monthly") return new Set(months);
  const isPeriodEnd = (month: string) => {
    const mm = month.slice(5, 7);
    return granularity === "yearly"
      ? mm === "12"
      : ["03", "06", "09", "12"].includes(mm);
  };
  const kept = months.filter(isPeriodEnd);
  const latest = months[months.length - 1];
  if (latest && !kept.includes(latest)) kept.push(latest);
  return new Set(kept);
}

function keepMonths(
  points: ReadonlyArray<Point>,
  keep: Set<string>
): Point[] {
  return points.filter((p) => keep.has(p.month));
}

const quarterLabel = (month: string) =>
  `Q${Math.ceil(Number(month.slice(5, 7)) / 3)} '${month.slice(2, 4)}`;
const yearLabel = (month: string) => month.slice(0, 4);

function CadenceToggle({
  value,
  onChange,
}: {
  value: Granularity;
  onChange: (granularity: Granularity) => void;
}) {
  return (
    <div className="absolute right-3 top-2 z-10 inline-flex overflow-hidden rounded border bg-white text-[11px]">
      {GRANULARITIES.map((g, i) => (
        <button
          key={g}
          type="button"
          onClick={() => onChange(g)}
          className={`px-2 py-0.5 capitalize ${i > 0 ? "border-l" : ""} ${
            value === g
              ? "bg-violet-600 text-white"
              : "text-gray-500 hover:bg-gray-50"
          }`}
        >
          {g}
        </button>
      ))}
    </div>
  );
}

function hasAnyValue(points: ReadonlyArray<Point>): boolean {
  return points.some((p) => p.value != null);
}

function UnitEconomicsSection({
  economics,
}: {
  economics: mrrPage_Query["response"]["unitEconomics"];
}) {
  const [paybackCadence, setPaybackCadence] = useState<Granularity>("monthly");
  const [cohortGrain, setCohortGrain] = useState<Granularity>("yearly");

  const payback = economics.cacPaybackSeries;
  const paybackKeep = periodEndMonths(
    payback.punctual.map((p) => p.month),
    paybackCadence
  );
  const paybackSeries = [
    {
      name: "Punctual",
      points: keepMonths(payback.punctual, paybackKeep),
      type: "line" as const,
      color: "#94a3b8",
    },
    {
      name: "TTM",
      points: keepMonths(payback.ttm, paybackKeep),
      type: "line" as const,
      color: "#7c3aed",
    },
  ];
  const paybackXLabel =
    paybackCadence === "quarterly"
      ? quarterLabel
      : paybackCadence === "yearly"
      ? yearLabel
      : undefined;
  const showPayback = hasAnyValue(payback.punctual) || hasAnyValue(payback.ttm);

  const cohorts = economics.cacPaybackCohorts[cohortGrain];
  const cohortSeries = [
    {
      name: "Months to recover CAC",
      points: cohorts.map((c) => ({ month: c.label, value: c.paybackMonths })),
      type: "bar" as const,
      color: "#2563eb",
    },
  ];
  const showCohorts = economics.cacPaybackCohorts.yearly.length > 0;

  const cards: {
    label: string;
    value: string;
    hint: string;
    explain?: ExplainMetric;
  }[] = [
    {
      label: "LTV",
      value: economics.ltv == null ? "—" : `$${economics.ltv.toLocaleString()}`,
      hint: "ARPU × gross margin ÷ churn",
      explain: "LTV",
    },
    {
      label: "CAC (TTM)",
      value:
        economics.cacTtm == null
          ? "—"
          : `$${economics.cacTtm.toLocaleString()}`,
      hint: "needs S&M categorized",
      explain: "CAC",
    },
    {
      label: "LTV : CAC",
      value: economics.ltvToCac == null ? "—" : economics.ltvToCac.toFixed(1),
      hint: "3+ is healthy",
    },
    {
      label: "CAC payback",
      value:
        economics.paybackMonths == null ? "—" : `${economics.paybackMonths} mo`,
      hint: "months to recover CAC",
    },
    {
      label: "Gross margin",
      value:
        economics.grossMarginTtm == null
          ? "—"
          : `${(economics.grossMarginTtm * 100).toFixed(0)}%`,
      hint: "TTM, from P&L",
      explain: "GROSS_MARGIN",
    },
    {
      label: "Rev churn",
      value:
        economics.monthlyRevenueChurnTtm == null
          ? "—"
          : `${(economics.monthlyRevenueChurnTtm * 100).toFixed(1)}%`,
      hint: "monthly, TTM average",
    },
  ];

  return (
    <div className="space-y-4">
      <h2 className="font-bold">Unit economics</h2>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        {cards.map((card) => (
          <div key={card.label} className="bg-white rounded border p-4">
            <div className="flex items-start justify-between gap-2">
              <p className="text-xs text-gray-500">{card.label}</p>
              {card.explain && <ExplainButton metric={card.explain} title={card.label} />}
            </div>
            <p className="text-2xl font-bold mt-1">{card.value}</p>
            <p className="text-[11px] text-gray-400 mt-1">{card.hint}</p>
          </div>
        ))}
      </div>

      <CohortCurveChart
        title="Average Revenue per Signup (ARPS) by cohort — cumulative revenue per signup, by months since signup"
        curves={economics.cohortCurves}
        format="money"
        hint="Each curve is one signup-year cohort: total normalized subscription revenue generated divided by the number of signups in that cohort, accumulating month by month as the cohort matures. Steeper / higher = better cohort quality. Toggle cohorts with the checkboxes."
      />

      <CohortCurveChart
        title="Paid-user churn by cohort — share of signups no longer active, by months since signup"
        curves={economics.churnCohortCurves}
        format="percent"
        hint="Each curve is one signup-year cohort: the fraction of that cohort's signups no longer active (1 − survival), by months since signup. Higher / steeper = faster churn — surfaces whether a cohort brought lower-quality users. The youngest cohort's tail is noisy (incomplete tenure). Toggle cohorts with the checkboxes."
      />

      <CohortCurveChart
        title="CAC payback by cohort — share of CAC recovered, by months since signup"
        curves={economics.cacPaybackCohortCurves}
        format="percent"
        referenceLine={{ value: 1, label: "paid back" }}
        hint="Each curve is one signup-year cohort: cumulative gross profit per signup as a share of that cohort's CAC per signup (the year's S&M ÷ signups). It crosses the 100% line at payback — the flatter/later the crossing, the longer that cohort takes to repay its acquisition cost. Toggle cohorts with the checkboxes."
      />

      <CohortCurveChart
        title="Plan-to-plan conversion by cohort — share upgraded above signup spend, by months since signup"
        curves={economics.conversionCohortCurves}
        format="percent"
        hint="Each curve is one signup-year cohort: the cumulative share whose monthly revenue has risen above their signup-month revenue (the MRR-expansion proxy for a plan upgrade). Higher / steeper = a cohort that converts up; a flat low curve = squatters who never upgrade. Toggle cohorts with the checkboxes."
      />

      {showPayback && (
        <div className="relative">
          <CadenceToggle value={paybackCadence} onChange={setPaybackCadence} />
          <KpiChart
            title="CAC payback period — months to recover CAC (gross-margin-adjusted)"
            series={paybackSeries}
            format="number"
            referenceLine={{ value: 12, label: "12 mo" }}
            formatXLabel={paybackXLabel}
            onMonthClick={() => {}}
          />
        </div>
      )}

      {showCohorts && (
        <div className="space-y-1">
          <div className="relative">
            <CadenceToggle value={cohortGrain} onChange={setCohortGrain} />
            <KpiChart
              title="CAC payback by acquisition cohort (months to recover)"
              series={cohortSeries}
              format="number"
              referenceLine={{ value: 12, label: "12 mo" }}
              onMonthClick={() => {}}
            />
          </div>
          <p className="text-[11px] text-gray-400">
            Each bar is one signup cohort: months until cumulative gross profit
            per customer repaid that cohort's CAC. Cohorts with no bar haven't
            recovered within the observed window.
          </p>
        </div>
      )}
    </div>
  );
}
